import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../common/Table";
import moment from "moment-timezone";
import { convertUtcToIst } from "../../common/TimeUtils";
import { exportToTablePDF } from "../../common/PdfFormat";
import { toast } from "react-toastify";
import {
  getWithdrawRequests,
  updateWithdrawStatus,
} from "../../redux/services/withdrawalRequestSlice";

const WithdrawRequest = () => {
  const dispatch = useDispatch();

  const withdrawData = useSelector(
    (state) => state?.withdrawRequest?.withdrawList
  );
  const [page, setPage] = useState(0); // same as Courses (0-indexed)
  const limitOptions = [
    { value: 10, label: 10 },
    { value: 30, label: 30 },
    { value: 50, label: 50 },
    { value: 100, label: 100 },
  ];
  const [pageLimit, setPageLimit] = useState(limitOptions[0]?.value);

  /** ========= FILTER & SEARCH ========== */
  const [filter, setFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchData, setSearchData] = useState("");
  const [loading, setLoading] = useState("");


  /** ==========================
   *  MODAL STATES
   * ========================== */
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(""); // "approve" | "reject"
  const [selectedRow, setSelectedRow] = useState(null);

  const [transactionId, setTransactionId] = useState("");
  const [description, setDescription] = useState("");



  /** ==========================
   *  FETCH WITHDRAW REQUESTS
   * ========================== */
  const getAllRequests = useCallback(async () => {
    try {
      await dispatch(
        getWithdrawRequests({
          page: page + 1,
          limit: pageLimit,
          isWithdrawRequestFilter: true,
          search: searchData,
          statusFilter: filter,
        })
      );
    } catch (error) { }
  }, [dispatch, searchData, filter, page, pageLimit]);

  useEffect(() => {
    getAllRequests();
  }, [getAllRequests]);

  /** ==========================
   *  OPEN APPROVE POPUP
   * ========================== */
  const openApprovePopup = (row) => {
    setSelectedRow(row);
    setModalType("success");
    setTransactionId(row.transactionId || "");
    setDescription("");
    setModalOpen(true);
  };

  /** ==========================
   *  OPEN REJECT POPUP
   * ========================== */
  const openRejectPopup = (row) => {
    setSelectedRow(row);
    setModalType("failed");
    setTransactionId("");
    setDescription("");
    setModalOpen(true);
  };

  /** ==========================
   *  SUBMIT APPROVE / REJECT
   * ========================== */
  const submitModal = async () => {

    try {
      setLoading(true)
      let payload = {
        id: selectedRow._id,
        transactionStatus: modalType === "success" ? "success" : "failed",
        description: description || "",
      };

      if (modalType === "success") {
        if (!transactionId.trim()) {
          toast.error("Please enter Transaction ID");
          return;
        }
        payload.transactionId = transactionId;
      }

      const response = await dispatch(updateWithdrawStatus(payload));
      setLoading(false)
      if (response?.payload?.status === 200) {
        toast.success(
          modalType === "success" ? "Request Approved" : "Request Rejected",
          { autoClose: 2000 }
        );
        setModalOpen(false);
        getAllRequests();
      }


    } catch (error) {

    }


  };

  /** ==========================
   *  COLUMNS
   * ========================== */
  const columns = [
    {
      header: "S/N",
      key: "index",
      render: (_, __, index) => page * pageLimit + index + 1,
    },
    // { header: "Title", key: "title" },
    { header: "Amount", key: "amount" },
    { header: "User Name", key: "user", render: (user) => `${user?.firstName} ${user?.lastName}` },
    { header: "User Email", key: "user", render: (user) => `${user?.email} ` },
    // { header: "Method", key: "method", render: (_, row) => row.method || "-" },
    { header: "Type", key: "type" },
    { header: "Payment Type", key: "paymentType" },
    {
      header: "Status", key: "status", render: (status) => <div className={`${status === "success" ? "text-green-500" : status === "failed" ? "text-red-500" : "text-yellow-500"}`}>
        {status}
      </div>
    },
    {
      header: "Date",
      key: "createdAt",
      render: (_, row) =>
        row.createdAt
          ? moment(convertUtcToIst(row.createdAt)).format("DD-MM-YYYY, hh:mm:ss A")
          : "-",
    },
    { header: "Account Holder Name", key: "paymentDetails", render: (paymentDetails) => `${paymentDetails?.accountHolderName ? paymentDetails?.accountHolderName : "-"} ` },
    { header: "Account Number", key: "paymentDetails", render: (paymentDetails) => `${paymentDetails?.accountNumber ? paymentDetails?.accountNumber : "-"} ` },
    { header: "Bank Name", key: "paymentDetails", render: (paymentDetails) => `${paymentDetails?.bankName ? paymentDetails?.bankName : "-"} ` },
    { header: "IFSC Code", key: "paymentDetails", render: (paymentDetails) => `${paymentDetails?.ifscCode ? paymentDetails?.ifscCode : "-"} ` },

    // { header: "Description", key: "description" },
  ];

  /** ==========================
   *  ACTION BUTTONS
   * ========================== */
  const actions = [
    {
      icon: (
        <div className="border p-2 rounded border-green-500 text-green-500 bg-green-10 ">
          A
        </div>
      ),
      disabled: (row) => row?.status !== "pending",
      onClick: (row) => openApprovePopup(row),
    },
    {
      icon: (
        <div className="border p-2 rounded border-red-500 text-red-500">
          R
        </div>
      ),
      disabled: (row) => row?.status !== "pending",
      onClick: (row) => openRejectPopup(row),
    },
  ];

  /** ==========================
   *  EXPORT PDF
   * ========================== */
  const exportToPDF = () => {
    const headersConfig = [
      { label: "S/N", key: "index", render: (_, __, index) => index + 1 },
      { label: "Title", key: "title" },
      { label: "Amount", key: "amount" },
      { label: "Method", key: "method" },
      { label: "Type", key: "earningType" },
      { label: "Txn Type", key: "transactionType" },
      {
        label: "Date",
        key: "createdAt",
        render: (_, row) =>
          row.createdAt
            ? moment(convertUtcToIst(row.createdAt)).format("DD-MM-YYYY")
            : "-",
      },
      { label: "Status", key: "status" },
      { label: "Description", key: "description" },
    ];

    const data = withdrawData?.balance_data || [];

    exportToTablePDF({
      tableName: "Withdraw Requests",
      data,
      headersConfig,
      pdfName: "withdraw_requests.pdf",
    });
  };

  const statusFilterOptions = [
    { value: "", label: "All" },
    { value: "pending", label: "Pending" },
    { value: "success", label: "Approved" },
    { value: "failed", label: "Rejected" },
  ];


  return (
    <div>
      <Table
        title="Withdrawal Requests"
        showSearchbar={true}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        setSearchData={setSearchData}
        filterOptions={statusFilterOptions}
        selectedFilter={filter}
        onFilterChange={(e) => setFilter(e ? e.value : "")}
        columns={columns}
        data={withdrawData?.balance_data}
        actions={actions}
        exportButton={true}
        onExport={exportToPDF}
        pageCount={withdrawData?.state?.page_limit} onPageChange={(selectedItem) => setPage(selectedItem.selected)} pageLimitOptions={limitOptions} selectedPageLimit={pageLimit} onPageLimitChange={(val) => {
          setPageLimit(val);
          setPage(0); // reset to page 0 like Courses
        }}
      />

      {/* ==========================
          APPROVE / REJECT POPUP
      ========================== */}
      {modalOpen && (
        <div className="fixed z-50 inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="bg-card rounded-xl p-6 w-[400px]">
            <h2 className="text-xl font-semibold mb-4">
              {modalType === "success"
                ? "Approve Withdrawal"
                : "Reject Withdrawal"}
            </h2>

            {/* APPROVE → SHOW TRANSACTION ID */}
            {modalType === "success" && (
              <input
                className="border rounded w-full p-2 mb-3"
                placeholder="Enter Transaction ID"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
              />
            )}

            {/* COMMON DESCRIPTION FIELD */}
            <textarea
              className="border rounded w-full p-2 h-24"
              placeholder="Enter Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <div className="flex justify-end gap-3 mt-4">
              <button
                className="px-4 py-2 bg-gray-300 rounded"
                onClick={() => setModalOpen(false)}

              >
                Cancel
              </button>

              <button
                className={`px-4 py-2 text-white rounded ${modalType === "success" ? "bg-green-500" : "bg-red-500"
                  } `}
                onClick={submitModal}
                disable={loading}
              >
                {loading ? "Loading..." : modalType === "success" ? "Approved" : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawRequest;
